import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    ArrowRight,
    ArrowUpRight,
    BrainCircuit,
    Cloud,
    Cog,
    Layers3,
    TrendingUp,
} from "lucide-react";

const impactItems = [
    {
        id: "01",
        title: "Build",
        heading: "Turn ideas into products people use.",
        category: "Product Engineering",
        description:
            "From concept to launch, we help businesses build scalable digital products around real users, real requirements and real business goals.",
        icon: Layers3,
        tag: "IDEA → PRODUCT",
    },
    {
        id: "02",
        title: "Automate",
        heading: "Make everyday work smarter.",
        category: "AI & Automation",
        description:
            "We bring AI and automation into the right parts of your business to reduce repetitive work, improve productivity and create smarter operations.",
        icon: BrainCircuit,
        tag: "WORK → INTELLIGENCE",
    },
    {
        id: "03",
        title: "Modernize",
        heading: "Move your technology forward.",
        category: "Cloud Engineering",
        description:
            "Modernize legacy environments with secure, scalable cloud architecture that gives your business the flexibility to adapt and grow.",
        icon: Cloud,
        tag: "LEGACY → CLOUD",
    },
    {
        id: "04",
        title: "Transform",
        heading: "Connect technology with operations.",
        category: "Enterprise Solutions",
        description:
            "Bring people, processes and platforms together to create connected operations that are easier to manage and built for scale.",
        icon: Cog,
        tag: "COMPLEXITY → CLARITY",
    },
    {
        id: "05",
        title: "Scale",
        heading: "Create technology that grows with you.",
        category: "Performance Engineering",
        description:
            "Improve performance, reliability and scalability so your technology infrastructure is ready for what's next.",
        icon: TrendingUp,
        tag: "TODAY → TOMORROW",
    },
];

const ClientImpactSection = () => {
    const [active, setActive] = useState(0);

    const current = impactItems[active];
    const Icon = current.icon;

    return (
        <section className="w-full bg-[#fff] py-[80px] lg:py-[25px] font-sans">
            <div className="max-w-[1440px] w-full mx-auto px-[20px] lg:px-[80px]">

                {/* =====================================================
                    HEADER
                ====================================================== */}

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="
                        grid
                        grid-cols-1
                        lg:grid-cols-[1fr_0.8fr]
                        gap-[35px]
                        lg:gap-[100px]
                        items-end
                        mb-[55px]
                        lg:mb-[70px]
                    "
                >
                    <div>
                        <div className="flex items-center gap-3 mb-[20px]">
                            <span className="w-[28px] h-[1px] bg-[#ED6800]" />

                            <span className="
                                font-graphik
                                text-[10px]
                                lg:text-[11px]
                                font-[600]
                                tracking-[0.18em]
                                uppercase
                                text-[#01386A]
                            ">
                                What We Help You Achieve
                            </span>
                        </div>

                        <h2 className="text-[#01386A] font-sora text-[20px] lg:text-[30px] font-[600] lg:mb-[60px] mb-[30px] relative z-10 leading-[1.2]">
                            Your challenge. Our technology.
                           
                        </h2>
                    </div>

                    <p className="
                        max-w-[480px]
                        lg:ml-auto
                        font-graphik
                        text-[#01386A]/60
                        text-[14px]
                        lg:text-[15px]
                        leading-[1.75]
                    ">
                        Every business is at a different stage. We bring the
                        right technology, expertise and execution to help you
                        move from where you are to where you want to be.
                    </p>
                </motion.div>


                {/* =====================================================
                    CAPABILITY NAVIGATION
                ====================================================== */}

                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    className="border-y border-[#01386A]/10"
                >
                    <div className="
                        grid
                        grid-cols-2
                        md:grid-cols-5
                    ">
                        {impactItems.map((item, index) => {
                            const isActive = active === index;

                            return (
                                <button
                                    key={item.id}
                                    onMouseEnter={() => setActive(index)}
                                    onClick={() => setActive(index)}
                                    className={`
                                        relative
                                        text-left
                                        px-[18px]
                                        py-[22px]
                                        lg:px-[24px]
                                        lg:py-[28px]
                                        transition-all
                                        duration-300
                                        border-b
                                        md:border-b-0
                                        md:border-r
                                        border-[#01386A]/10
                                        last:border-r-0
                                        group
                                    `}
                                >
                                    {/* Active Line */}

                                    <span
                                        className={`
                                            absolute
                                            left-0
                                            bottom-0
                                            h-[2px]
                                            bg-[#ED6800]
                                            transition-all
                                            duration-300
                                            ${
                                                isActive
                                                    ? "w-full"
                                                    : "w-0 group-hover:w-full"
                                            }
                                        `}
                                    />

                                    <div className="flex items-start justify-between gap-3">

                                        <div>
                                            <span
                                                className={`
                                                    block
                                                    font-graphik
                                                    text-[9px]
                                                    tracking-[0.15em]
                                                    uppercase
                                                    mb-[7px]
                                                    transition-colors
                                                    ${
                                                        isActive
                                                            ? "text-[#ED6800]"
                                                            : "text-[#01386A]/30"
                                                    }
                                                `}
                                            >
                                                {item.id}
                                            </span>

                                            <span
                                                className={`
                                                    block
                                                    font-sora
                                                    text-[15px]
                                                    lg:text-[17px]
                                                    font-[600]
                                                    transition-colors
                                                    ${
                                                        isActive
                                                            ? "text-[#01386A]"
                                                            : "text-[#01386A]/45"
                                                    }
                                                `}
                                            >
                                                {item.title}
                                            </span>
                                        </div>

                                        <ArrowUpRight
                                            size={15}
                                            strokeWidth={1.7}
                                            className={`
                                                mt-[3px]
                                                transition-all
                                                duration-300
                                                ${
                                                    isActive
                                                        ? "text-[#ED6800] translate-x-[2px] -translate-y-[2px]"
                                                        : "text-[#01386A]/20"
                                                }
                                            `}
                                        />

                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </motion.div>


                {/* =====================================================
                    MAIN CONTENT
                ====================================================== */}

                <div className="
                    mt-[45px]
                    lg:mt-[65px]
                    border
                    border-[#01386A]/10
                    bg-white
                ">

                    <AnimatePresence mode="wait">

                        <motion.div
                            key={current.id}
                            initial={{
                                opacity: 0,
                                y: 15,
                            }}
                            animate={{
                                opacity: 1,
                                y: 0,
                            }}
                            exit={{
                                opacity: 0,
                                y: -15,
                            }}
                            transition={{
                                duration: 0.35,
                            }}
                            className="
                                grid
                                grid-cols-1
                                lg:grid-cols-[0.85fr_1.15fr]
                            "
                        >

                            {/* =================================================
                                LEFT INFORMATION
                            ================================================== */}

                            <div className="
                                p-[28px]
                                sm:p-[40px]
                                lg:p-[55px]
                                border-b
                                lg:border-b-0
                                lg:border-r
                                border-[#01386A]/10
                            ">

                                <div className="flex items-center justify-between">

                                    <div className="
                                        flex
                                        items-center
                                        gap-3
                                    ">
                                        <div className="
                                            w-[42px]
                                            h-[42px]
                                            flex
                                            items-center
                                            justify-center
                                            border
                                            border-[#01386A]/10
                                            bg-[#F7F9FB]
                                        ">
                                            <Icon
                                                size={18}
                                                strokeWidth={1.5}
                                                className="text-[#01386A]"
                                            />
                                        </div>

                                        <span className="
                                            font-graphik
                                            text-[10px]
                                            uppercase
                                            tracking-[0.15em]
                                            text-[#01386A]/45
                                        ">
                                            {current.category}
                                        </span>
                                    </div>

                                    <span className="
                                        font-sora
                                        text-[13px]
                                        text-[#01386A]/25
                                    ">
                                        {current.id}
                                    </span>

                                </div>


                                <div className="mt-[70px] lg:mt-[110px]">

                                    <span className="
                                        font-graphik
                                        text-[10px]
                                        uppercase
                                        tracking-[0.16em]
                                        text-[#ED6800]
                                    ">
                                        Capability
                                    </span>

                                    <h3 className="
                                        mt-[15px]
                                        font-sora
                                        text-[#01386A]
                                        text-[32px]
                                        sm:text-[40px]
                                        lg:text-[50px]
                                        font-[500]
                                        leading-[1.08]
                                        tracking-[-0.035em]
                                    ">
                                        {current.title}
                                    </h3>

                                </div>

                            </div>


                            {/* =================================================
                                RIGHT CONTENT
                            ================================================== */}

                            <div className="
                                p-[28px]
                                sm:p-[40px]
                                lg:p-[55px]
                                flex
                                flex-col
                                justify-between
                                min-h-[400px]
                                lg:min-h-[500px]
                            ">

                                <div>

                                    <span className="
                                        font-graphik
                                        text-[10px]
                                        uppercase
                                        tracking-[0.16em]
                                        text-[#01386A]/40
                                    ">
                                        {current.category}
                                    </span>

                                    <h4 className="
                                        mt-[18px]
                                        max-w-[700px]
                                        font-sora
                                        text-[#01386A]
                                        text-[27px]
                                        sm:text-[35px]
                                        lg:text-[44px]
                                        font-[500]
                                        leading-[1.12]
                                        tracking-[-0.025em]
                                    ">
                                        {current.heading}
                                    </h4>

                                    <p className="
                                        mt-[25px]
                                        max-w-[620px]
                                        font-graphik
                                        text-[#01386A]/55
                                        text-[14px]
                                        lg:text-[15px]
                                        leading-[1.8]
                                    ">
                                        {current.description}
                                    </p>

                                </div>


                                {/* Bottom Information */}

                                <div className="
                                    mt-[60px]
                                    pt-[25px]
                                    border-t
                                    border-[#01386A]/10
                                    flex
                                    flex-col
                                    sm:flex-row
                                    sm:items-center
                                    sm:justify-between
                                    gap-[25px]
                                ">

                                    <div>

                                        <span className="
                                            block
                                            font-graphik
                                            text-[9px]
                                            uppercase
                                            tracking-[0.15em]
                                            text-[#01386A]/30
                                            mb-[9px]
                                        ">
                                            Transformation
                                        </span>

                                        <div className="
                                            flex
                                            items-center
                                            gap-2
                                        ">
                                            <span className="
                                                font-sora
                                                text-[11px]
                                                text-[#01386A]/65
                                            ">
                                                {current.tag.split(" → ")[0]}
                                            </span>

                                            <ArrowRight
                                                size={13}
                                                className="text-[#ED6800]"
                                            />

                                            <span className="
                                                font-sora
                                                text-[11px]
                                                text-[#01386A]/65
                                            ">
                                                {current.tag.split(" → ")[1]}
                                            </span>
                                        </div>

                                    </div>


                                    <button
                                        className="
                                            group
                                            flex
                                            items-center
                                            gap-3
                                            font-graphik
                                            text-[10px]
                                            uppercase
                                            tracking-[0.14em]
                                            text-[#01386A]
                                        "
                                    >
                                        Explore capability

                                        <span className="
                                            w-[34px]
                                            h-[34px]
                                            flex
                                            items-center
                                            justify-center
                                            border
                                            border-[#01386A]/15
                                            transition-all
                                            duration-300
                                            group-hover:bg-[#ED6800]
                                            group-hover:border-[#ED6800]
                                        ">
                                            <ArrowUpRight
                                                size={14}
                                                strokeWidth={1.5}
                                                className="
                                                    transition-transform
                                                    duration-300
                                                    group-hover:text-white
                                                    group-hover:translate-x-[1px]
                                                    group-hover:-translate-y-[1px]
                                                "
                                            />
                                        </span>
                                    </button>

                                </div>

                            </div>

                        </motion.div>

                    </AnimatePresence>

                </div>


             
              
            </div>
        </section>
    );
};

export default ClientImpactSection;